import { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X } from 'lucide-react';

const LANG_LABELS = { en: 'EN', hi: 'हि', pa: 'ਪੰ' };

export default function Navbar() {
  const { t, lang, setLang } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const navLinks = [
    { to: '/', label: t.nav.home, testId: 'nav-home' },
    { to: '/products', label: t.nav.products, testId: 'nav-products' },
    { to: '/about', label: t.nav.about, testId: 'nav-about' },
    { to: '/dealers', label: t.nav.dealers, testId: 'nav-dealers' },
    { to: '/gallery', label: t.nav.gallery, testId: 'nav-gallery' },
    { to: '/contact', label: t.nav.contact, testId: 'nav-contact' },
  ];

  return (
    <header data-testid="navbar" className="sticky top-0 z-50 backdrop-blur-xl border-b border-black/5" style={{ backgroundColor: 'rgba(255,255,255,0.92)' }}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">

          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 flex-shrink-0">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/2a6bfe0d-bf62-4b21-995b-6c2794398f74/images/5366a31807515758e52f2da5be6a24582972673174d92b993737d70b7babc123.png"
              alt="ARV Shree Ram Agro Industries Logo"
              className="w-10 h-10 object-contain"
            />
            <div className="hidden sm:block">
              <div className="font-heading font-bold text-sm leading-none" style={{ color: '#0A3F22' }}>SHREE RAM</div>
              <div className="font-heading font-semibold text-xs leading-none mt-0.5" style={{ color: '#D97706' }}>AGRO INDUSTRIES</div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={link.testId}
                end={link.to === '/'}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium rounded transition-colors duration-200 font-body ${
                    isActive
                      ? 'text-forest-900 bg-forest-50 font-semibold'
                      : 'text-gray-700 hover:text-forest-900 hover:bg-forest-50'
                  }`
                }
                style={({ isActive }) => isActive ? { color: '#0A3F22', backgroundColor: '#EAF2EC' } : {}}
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* Language Switcher */}
            <div className="flex items-center gap-0.5 border rounded-md overflow-hidden" style={{ borderColor: '#E5E7EB' }}>
              {Object.entries(LANG_LABELS).map(([code, label]) => (
                <button
                  key={code}
                  data-testid={`lang-${code}`}
                  onClick={() => setLang(code)}
                  className={`px-2.5 py-1 text-xs font-semibold transition-all duration-200 ${
                    lang === code ? 'text-white' : 'text-gray-600 hover:text-gray-900'
                  }`}
                  style={lang === code ? { backgroundColor: '#0A3F22' } : { backgroundColor: 'transparent' }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* CTA Button */}
            <Link
              to="/contact"
              data-testid="nav-cta"
              className="hidden md:flex px-4 py-2 text-sm font-semibold text-white rounded transition-all duration-200 hover:opacity-90 font-heading"
              style={{ backgroundColor: '#D97706' }}
            >
              {t.nav.cta}
            </Link>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 rounded text-gray-700 hover:bg-gray-100"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden mobile-menu pb-4 border-t border-gray-100 mt-2 pt-2">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`mobile-${link.testId}`}
                end={link.to === '/'}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `block px-4 py-3 text-sm font-medium rounded mb-1 transition-colors duration-200 ${
                    isActive ? 'font-semibold' : 'text-gray-700'
                  }`
                }
                style={({ isActive }) => isActive ? { color: '#0A3F22', backgroundColor: '#EAF2EC' } : {}}
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              onClick={() => setMobileOpen(false)}
              className="block mt-2 mx-4 px-4 py-2.5 text-center text-sm font-semibold text-white rounded"
              style={{ backgroundColor: '#D97706' }}
            >
              {t.nav.cta}
            </Link>
          </div>
        )}
      </div>
    </header>
  );
}
